import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { Spinner } from "@radix-ui/themes";
import { GlobalContext } from "../GlobalContext";
import { emitter } from "../UtilityFunctions";
import { apiFetch } from "../api.js";
import useDebouncedValue from "../hooks/useDebouncedValue";

const MIN_QUERY_LENGTH = 2; //Matches the server's floor in routes/search.js
const DEBOUNCE_MS = 250;

//Which sheet a result lives on. Colours follow the app's existing semantics:
//green = live/actionable, blue = money in, amber = owed, grey = closed/dead.
const SHEET_BADGES = {
    Inventory:     { label: "Inventory",      className: "bg-green-100 text-green-800" },
    Committed:     { label: "Committed",      className: "bg-indigo-100 text-indigo-800" },
    Cash:          { label: "Cash",           className: "bg-blue-100 text-blue-800" },
    Credit:        { label: "Credit",         className: "bg-amber-100 text-amber-800" },
    CreditCleared: { label: "Credit Cleared", className: "bg-slate-100 text-slate-700" },
    Cancelled:     { label: "Cancelled",      className: "bg-rose-100 text-rose-700" },
};

const resultKey = (r) => `${r.sheet}:${r.DealAgreementNo ?? r.VehicleID}`;

//"Toyota Premio 2015" — skip the blanks rather than render "undefined".
const describe = (r) => [r.Make, r.Model, r.ModelYear].filter(Boolean).join(" ") || "Vehicle";
const subtitle = (r) => [r.ChassisNo, r.NumberPlate, r.Color, r.CustomerName].filter(Boolean).join(" · ");

/**
 * Universal vehicle search for the app header. Finds a vehicle by chassis
 * number, plate, make, model, year or colour across every sheet — including the
 * server-paginated ones (Cash / Credit Cleared / Cancelled), whose off-page rows
 * no per-sheet search box can reach.
 *
 * Selecting a result emits `open-vehicle`; SubNav resolves it (cache first,
 * server second) and opens the vehicle's modal.
 *
 * Prop-free by design apart from presentation: it reads context itself and
 * reports results through the emitter, so the header threads no state.
 */
export default function GlobalSearch({ className = "", autoFocus = false, onDismiss }) {
    const { DealershipID } = useContext(GlobalContext);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [status, setStatus] = useState("idle"); //idle | loading | ok | empty | error
    const [activeIndex, setActiveIndex] = useState(0);
    const [focused, setFocused] = useState(false);

    const inputRef = useRef(null);
    const rootRef = useRef(null);
    const listRef = useRef(null);

    const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);
    const trimmed = debouncedQuery.trim();
    const open = focused && trimmed.length >= MIN_QUERY_LENGTH && status !== "idle";

    useEffect(() => {
        if (trimmed.length < MIN_QUERY_LENGTH) {
            setResults([]);
            setStatus("idle");
            return;
        }

        //Abort in-flight requests so a slow early keystroke can't overwrite a
        //fast later one. The `cancelled` flag covers the gap where abort()
        //rejects asynchronously after the response has already resolved.
        const controller = new AbortController();
        let cancelled = false;
        setStatus("loading");

        (async () => {
            try {
                const res = await apiFetch("/search/vehicles", {
                    body: { q: trimmed },
                    signal: controller.signal,
                });
                const payload = await res.json();
                if (cancelled) return;
                if (!res.ok || !payload.ok) throw new Error(payload.message || `HTTP ${res.status}`);
                const rows = payload.results || [];
                setResults(rows);
                setActiveIndex(0);
                setStatus(rows.length ? "ok" : "empty");
            } catch (err) {
                if (cancelled || err.name === "AbortError") return;
                setResults([]);
                setStatus("error");
            }
        })();

        return () => { cancelled = true; controller.abort(); };
    }, [trimmed, DealershipID]);

    //Keep the keyboard-highlighted row in view when the list scrolls.
    useEffect(() => {
        if (!open || !listRef.current) return;
        listRef.current.querySelector(`[data-index="${activeIndex}"]`)
            ?.scrollIntoView({ block: "nearest" });
    }, [activeIndex, open]);

    const close = useCallback(() => {
        setFocused(false);
        inputRef.current?.blur();
    }, []);

    const reset = useCallback(() => {
        setQuery("");
        setResults([]);
        setStatus("idle");
    }, []);

    const select = useCallback((result) => {
        if (!result) return;
        emitter.emit("open-vehicle", {
            sheet: result.sheet,
            VehicleID: result.VehicleID,
            DealAgreementNo: result.DealAgreementNo,
        });
        reset();
        close();
        onDismiss?.();
    }, [reset, close, onDismiss]);

    //Dismiss on an outside click, matching NotificationBell's dropdown.
    useEffect(() => {
        if (!focused) return;
        const handler = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setFocused(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [focused]);

    //Global shortcuts. Ctrl/Cmd+K is always safe; "/" only when the user isn't
    //already typing — this app is mostly data-entry forms, and hijacking "/"
    //inside a chassis-number field would be hostile.
    useEffect(() => {
        const handler = (e) => {
            const isShortcut = (e.key === "k" || e.key === "K") && (e.ctrlKey || e.metaKey);
            const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || "")
                || document.activeElement?.isContentEditable;
            if (!isShortcut && !(e.key === "/" && !typing)) return;
            e.preventDefault();
            inputRef.current?.focus();
            inputRef.current?.select();
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, []);

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            if (!results.length) return;
            e.preventDefault(); //Otherwise the caret jumps to the ends of the input
            const delta = e.key === "ArrowDown" ? 1 : -1;
            setActiveIndex((i) => Math.min(results.length - 1, Math.max(0, i + delta)));
        } else if (e.key === "Enter") {
            if (open && results.length) {
                e.preventDefault();
                select(results[activeIndex]);
            }
        } else if (e.key === "Escape") {
            e.preventDefault();
            //Two-stage: clear the query first, close only once it's empty.
            if (query) reset();
            else { close(); onDismiss?.(); }
        } else if (e.key === "Tab") {
            setFocused(false);
        }
    };

    return (
        <div ref={rootRef} className={`relative ${className}`} role="combobox"
             aria-expanded={open} aria-haspopup="listbox" aria-owns="global-search-listbox">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--theme-muted)]" />
            <input
                ref={inputRef}
                type="search"
                //type=search keeps the "Search" key on mobile keyboards, but its
                //native cancel widget would sit beside our own clear button.
                className="input-t w-full [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
                //Inline, not pl-9/pr-8: theme.css loads after Tailwind and
                //.input-t's `padding` shorthand wins over the utilities, which
                //would leave the magnifier sitting on top of the placeholder.
                style={{ paddingLeft: 34, paddingRight: 32 }}
                placeholder="Search vehicles…"
                aria-label="Search vehicles across all sheets"
                aria-autocomplete="list"
                aria-controls="global-search-listbox"
                aria-activedescendant={open && results.length ? `global-search-option-${activeIndex}` : undefined}
                autoFocus={autoFocus}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onKeyDown={onKeyDown}
            />
            {query && (
                <button
                    type="button"
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--theme-muted)] hover:text-[var(--theme-fg)]"
                    onMouseDown={(e) => e.preventDefault()} //Keep focus so the list doesn't flash closed
                    onClick={() => { reset(); inputRef.current?.focus(); }}
                >
                    <X className="h-4 w-4" />
                </button>
            )}

            {open && (
                <div
                    ref={listRef}
                    id="global-search-listbox"
                    role="listbox"
                    aria-label="Vehicle search results"
                    className="absolute left-0 right-0 top-full mt-1 z-[60] max-h-[70vh] md:max-h-[60vh] overflow-y-auto overscroll-contain rounded-[var(--theme-radius-lg)] border border-[var(--theme-line)] bg-[var(--theme-surface)] shadow-[var(--theme-shadow-dialog)]"
                >
                    {status === "loading" && (
                        <div className="flex items-center gap-2 px-3 py-3 text-sm text-[var(--theme-muted)]">
                            <Spinner size="1" /> Searching…
                        </div>
                    )}
                    {status === "empty" && (
                        <div className="px-3 py-3 text-sm text-[var(--theme-muted)]">
                            No vehicles match “{trimmed}”.
                        </div>
                    )}
                    {status === "error" && (
                        <div className="px-3 py-3 text-sm text-red-600">Search failed. Try again.</div>
                    )}
                    {status === "ok" && results.map((r, i) => {
                        const badge = SHEET_BADGES[r.sheet] || { label: r.sheet, className: "bg-slate-100 text-slate-700" };
                        return (
                            <div
                                key={resultKey(r)}
                                id={`global-search-option-${i}`}
                                data-index={i}
                                role="option"
                                aria-selected={i === activeIndex}
                                //mousedown, not click: the input's blur would tear
                                //the list down before a click ever landed.
                                onMouseDown={(e) => { e.preventDefault(); select(r); }}
                                onMouseEnter={() => setActiveIndex(i)}
                                className={`cursor-pointer px-3 py-2 border-b border-[var(--theme-line)] last:border-b-0 ${
                                    i === activeIndex ? "bg-[var(--theme-accent-tint)]" : ""
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className={`flex-1 min-w-0 truncate text-sm font-medium ${
                                        i === activeIndex ? "text-[var(--theme-accent-ink)]" : "text-[var(--theme-fg)]"
                                    }`}>
                                        {describe(r)}
                                    </span>
                                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.className}`}>
                                        {badge.label}
                                    </span>
                                </div>
                                <div className="truncate text-xs text-[var(--theme-muted)]">{subtitle(r)}</div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
